/* @jsx jsx */
import { css, jsx } from '@emotion/react';

import { Suspense, lazy, useCallback, useMemo } from 'react';

import type {
  PlayStateChangeEvent,
  Posters,
  SourcesByResolution,
  SubtitlesVtt,
  VideoPlayerHandle,
} from '@coursera-web/video-player';

import { Skeleton, breakpoints } from '@coursera/cds-core';
import { useTracker } from '@coursera/event-pulse/react';

import TrackedDiv from 'bundles/page/components/TrackedDiv';
import type { Maybe } from 'bundles/premium-hub/__generated__/baseContentfulTypes';
import type {
  PremiumHubAudioDescriptionAssetFragment,
  PremiumHubImageAssetFragment,
  PremiumHubVideoAssetFragment,
} from 'bundles/premium-hub/__generated__/contentfulTypes';

import _t from 'i18n!nls/premium-hub';

// VideoPlayer pulls in the video.js runtime (~140kB before gzip). Defer it so it
// stays out of the premium-hub bundle until a testimonial video actually renders.
const VideoPlayer = lazy(() => import('bundles/premium-hub/components/shared/VideoPlayer'));

type Props = {
  videoFile: PremiumHubVideoAssetFragment;
  audioDescriptionTrack?: Maybe<PremiumHubAudioDescriptionAssetFragment>;
  placeholderImage: PremiumHubImageAssetFragment;
  index: number;
  authorName?: string | null;
  authorTitle?: string | null;
  setVideoPlayer: (player: VideoPlayerHandle | null) => void;
  text?: string | null;
  slideNumber: number;
};

const styles = {
  root: css`
    position: relative;
    aspect-ratio: 16 / 9;
    overflow: hidden;
    border-radius: var(--cds-border-radius-100) var(--cds-border-radius-100) 0 0;

    ${breakpoints.up('md')} {
      aspect-ratio: unset;
      height: 434px;
      border-radius: var(--cds-border-radius-100) 0 0 var(--cds-border-radius-100);
    }
  `,

  player: css`
    --video-player-border-radius: 0;

    &,
    picture {
      background-color: var(--cds-color-neutral-primary);
    }

    border: none;
  `,

  visibilityOverlay: css`
    position: absolute;
    inset: 0;
    pointer-events: none;
  `,
};

const TestimonialVideo = ({
  videoFile,
  setVideoPlayer,
  audioDescriptionTrack,
  placeholderImage,
  index,
  authorName,
  authorTitle,
  text,
  slideNumber,
}: Props) => {
  const trackV3 = useTracker();

  const sources = useMemo<SourcesByResolution | undefined>(
    () => (videoFile.url ? { '720p': { 'video/mp4': videoFile.url } } : undefined),
    [videoFile.url]
  );

  const posters = useMemo<Posters | undefined>(
    () => (placeholderImage?.url ? { '720p': placeholderImage.url } : undefined),
    [placeholderImage?.url]
  );

  const subtitlesVtt = useMemo<SubtitlesVtt | undefined>(() => {
    if (!audioDescriptionTrack?.url) {
      return undefined;
    }

    return {
      captions: {
        label: _t('Captions'),
        url: audioDescriptionTrack.url,
      },
    };
  }, [audioDescriptionTrack?.url]);

  const handlePlayStateChange = useCallback<(event: PlayStateChangeEvent) => void>(
    (event) => {
      const testimonialCard = { author: authorName ?? '', text: text ?? '', index: index ?? -1 };
      if (event.action === 'play' && event.source === 'centerPlayButton') {
        trackV3('click_testimonial_card', { testimonialCard, testimonialAction: 'play_video' });
      } else if (event.action === 'pause') {
        trackV3('click_testimonial_card', { testimonialCard, testimonialAction: 'pause_video' });
      }
    },
    [trackV3, authorName, text, index]
  );

  if (!sources || !placeholderImage) {
    return null;
  }

  return (
    <div css={styles.root}>
      <Suspense fallback={<Skeleton variant="rect" height="100%" width="100%" />}>
        <VideoPlayer
          ref={setVideoPlayer}
          css={styles.player}
          sources={sources}
          posters={posters}
          subtitlesVtt={subtitlesVtt}
          layoutMode="fill"
          crossOrigin="anonymous"
          disableGlobalHotkeys
          name={authorName ?? ''}
          ariaLabel={_t('Slide #{slideNumber}. Testimonial video, #{description}', {
            slideNumber,
            description: placeholderImage.description,
          })}
          sentryErrorSource="TestimonialVideo"
          showSubtitlesToggle
          onPlayStateChange={handlePlayStateChange}
        >
          <TrackedDiv
            trackingName="testimonial"
            withVisibilityTracking
            data={{ indexPosition: index + 1, testimonialName: authorName ?? '', testimonialTitle: authorTitle ?? '' }}
            css={styles.visibilityOverlay}
          />
        </VideoPlayer>
      </Suspense>
    </div>
  );
};
export default TestimonialVideo;
